/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * Voice Toast Service - Toast notification helpers for voice features
 *
 * @fileoverview Voice-specific toast notification service
 * @version 1.0.0
 * @since v0.0.08
 *
 * PURPOSE:
 * Provides centralized toast notification functions for voice session events
 * including errors, mode changes, and status updates. Uses a simple callback
 * pattern to integrate with any toast library (Chakra UI, react-toastify, etc.)
 *
 * KEY RESPONSIBILITIES:
 * - Voice session error toasts
 * - Mode change notifications
 * - Connection status toasts
 * - Relay status toasts
 *
 * DEPENDENCIES:
 * - voiceSessionService for error codes
 * - voiceMessages for localized strings
 *
 * CHANGE LOG:
 * 2026-01-05 - Agent A - Initial implementation
 */

import { VoiceSessionErrorCode, getErrorMessage } from '../voice/voiceSessionService';
import { voiceToasts } from '../../i18n/voiceMessages';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Toast status types (compatible with Chakra UI and similar libraries)
 */
export type ToastStatus = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast configuration for display
 */
export interface ToastConfig {
  title: string;
  description?: string;
  status: ToastStatus;
  duration?: number;
  isClosable?: boolean;
}

/**
 * Toast display function signature
 * Can be set to the toast function from your UI library
 */
export type ToastDisplayFn = (config: ToastConfig) => void;

// ============================================================================
// TOAST SERVICE CLASS
// ============================================================================

/**
 * Voice Toast Service
 *
 * Manages toast notifications for voice session events.
 * Must be initialized with a toast display function before use.
 *
 * @example
 * // In a React component with Chakra UI
 * import { useToast } from '@chakra-ui/react';
 * import { voiceToastService } from '../services/toast/voiceToastService';
 *
 * const toast = useToast();
 * voiceToastService.setToastFn(toast);
 *
 * // Later, show error toast
 * voiceToastService.showError(VoiceSessionErrorCode.SESSION_FULL);
 */
class VoiceToastService {
  private toastFn: ToastDisplayFn | null = null;
  private defaultDuration = 5000;

  /**
   * Set the toast display function
   *
   * Must be called before any toast methods. Typically called once
   * at the app level or in a context provider.
   *
   * @param fn - Toast display function (e.g., from useToast hook)
   */
  setToastFn(fn: ToastDisplayFn): void {
    this.toastFn = fn;
  }

  /**
   * Check if toast function is configured
   */
  isConfigured(): boolean {
    return this.toastFn !== null;
  }

  /**
   * Display a toast notification
   *
   * @param config - Toast configuration
   */
  private show(config: ToastConfig): void {
    if (!this.toastFn) {
      console.warn('[VoiceToastService] Toast function not configured. Call setToastFn first.');
      console.log('[VoiceToastService] Would show toast:', config);
      return;
    }

    this.toastFn({
      ...config,
      duration: config.duration ?? this.defaultDuration,
      isClosable: config.isClosable ?? true
    });
  }

  // ==========================================================================
  // ERROR TOASTS
  // ==========================================================================

  /**
   * Show voice session error toast
   *
   * Displays a user-friendly error message based on the error code.
   * Uses the error messages from voiceMessages.ts.
   *
   * @param errorCode - VoiceSessionErrorCode from API response
   * @param customMessage - Optional custom message override
   *
   * @example
   * voiceToastService.showError(VoiceSessionErrorCode.SESSION_FULL);
   * // Shows: "Session full (6/6 participants). Try again later."
   */
  showError(errorCode: VoiceSessionErrorCode, customMessage?: string): void {
    const message = customMessage || getErrorMessage(errorCode);
    
    this.show({
      title: voiceToasts.error.title,
      description: message,
      status: 'error',
      duration: 7000
    });
  }

  /**
   * Show network error toast
   *
   * Convenience method for network-related errors.
   */
  showNetworkError(): void {
    this.showError(VoiceSessionErrorCode.NETWORK_ERROR);
  }

  /**
   * Show session full error toast
   *
   * Convenience method for session capacity errors.
   */
  showSessionFullError(): void {
    this.showError(VoiceSessionErrorCode.SESSION_FULL);
  }

  /**
   * Show authorization error toast
   *
   * Convenience method for permission errors.
   */
  showAuthorizationError(): void {
    this.showError(VoiceSessionErrorCode.NOT_AUTHORIZED);
  }

  // ==========================================================================
  // MODE CHANGE TOASTS
  // ==========================================================================

  /**
   * Show voice mode changed toast
   *
   * Displays notification when the voice mode changes.
   * Used when GM changes mode and WebSocket broadcasts the change.
   *
   * @param newMode - The new voice mode label
   *
   * @example
   * voiceToastService.showModeChanged('Hey MM™');
   * // Shows: "Voice Mode Changed - Mode changed to: Hey MM™"
   */
  showModeChanged(newMode: string): void {
    this.show({
      title: voiceToasts.modeChanged.title,
      description: `${voiceToasts.modeChanged.description} ${newMode}`,
      status: 'info',
      duration: 3000
    });
  }

  // ==========================================================================
  // CONNECTION STATUS TOASTS
  // ==========================================================================

  /**
   * Show voice session joined toast
   *
   * Displays success notification when joining a voice session.
   *
   * @param sessionInfo - Optional session info to display
   */
  showJoined(sessionInfo?: string): void {
    this.show({
      title: voiceToasts.joined.title,
      description: sessionInfo || voiceToasts.joined.description,
      status: 'success',
      duration: 3000
    });
  }

  /**
   * Show voice session left toast
   *
   * Displays notification when leaving a voice session.
   */
  showLeft(): void {
    this.show({
      title: voiceToasts.left.title,
      description: voiceToasts.left.description,
      status: 'info',
      duration: 2000
    });
  }

  /**
   * Show reconnecting toast
   *
   * Displays warning when attempting to reconnect.
   */
  showReconnecting(): void {
    this.show({
      title: voiceToasts.reconnecting.title,
      description: voiceToasts.reconnecting.description,
      status: 'warning',
      duration: 4000
    });
  }

  /**
   * Show reconnected toast
   *
   * Displays success when reconnection succeeds.
   */
  showReconnected(): void {
    this.show({
      title: voiceToasts.reconnected.title,
      description: voiceToasts.reconnected.description,
      status: 'success',
      duration: 3000
    });
  }

  // ==========================================================================
  // RELAY STATUS TOASTS
  // ==========================================================================

  /**
   * Show Discord relay enabled toast
   *
   * Displays notification when Discord relay is enabled.
   */
  showRelayEnabled(): void {
    this.show({
      title: voiceToasts.relay.enabled.title,
      description: voiceToasts.relay.enabled.description,
      status: 'success',
      duration: 4000
    });
  }

  /**
   * Show Discord relay disabled toast
   *
   * Displays notification when Discord relay is disabled.
   */
  showRelayDisabled(): void {
    this.show({
      title: voiceToasts.relay.disabled.title,
      description: voiceToasts.relay.disabled.description,
      status: 'info',
      duration: 3000
    });
  }

  /**
   * Show Discord relay connection failed toast
   *
   * Displays error when relay connection fails.
   */
  showRelayFailed(): void {
    this.show({
      title: voiceToasts.relay.failed.title,
      description: voiceToasts.relay.failed.description,
      status: 'error',
      duration: 5000
    });
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Singleton instance of VoiceToastService
 * Use this for all voice-related toast notifications
 */
export const voiceToastService = new VoiceToastService();

export default voiceToastService;

